import {
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import { useControllableState } from '../../../core/hooks/useControllableState.js';

interface UseBreakpointCollapseArgs {
  /** Controlled / uncontrolled props for the collapsed state. */
  collapsed: {
    value: boolean | undefined;
    defaultValue: boolean;
    onChange: ((value: boolean) => void) | undefined;
  };
  /** Container width below which the panel auto-collapses. */
  breakpoint: number;
  /** Container element observed for size changes. */
  containerRef: RefObject<HTMLDivElement>;
}

interface UseBreakpointCollapseResult {
  /** Most recent measured container width (`0` until first measurement). */
  containerWidth: number;
  /** Current collapsed state (controlled value if provided). */
  collapsed: boolean;
  /** Setter for the collapsed state. */
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  /** True once measured and the container is narrower than `breakpoint`. */
  isBelowBreakpoint: boolean;
}

/**
 * Tracks the container width and collapses the panel when the container
 * crosses the configured breakpoint. Shared between the details-layout and
 * sidebar-layout `useLayoutState` hooks; both wrap this and add their own
 * mode-resolution logic on top.
 *
 * Only emits `onCollapsedChange` when the container *crosses* the threshold
 * (not on every resize while it stays on the same side), and skips the
 * first-measurement emit when the value already matches the measured side.
 *
 * @internal
 */
export function useBreakpointCollapse({
  collapsed: collapsedProps,
  breakpoint,
  containerRef,
}: UseBreakpointCollapseArgs): UseBreakpointCollapseResult {
  const [containerWidth, setContainerWidth] = useState(0);

  const [collapsed, setCollapsed] = useControllableState({
    value: collapsedProps.value,
    defaultValue: collapsedProps.defaultValue,
    onChange: collapsedProps.onChange,
  });

  // Tracks which side of the breakpoint the container was on at the previous
  // measurement. `null` until the first measurement so we can distinguish
  // "first ever measurement" from "stayed on the same side".
  const prevBelowBreakpointRef = useRef<boolean | null>(null);

  useResizeObserver({
    ref: containerRef,
    onResize: (size) => {
      if (!size.width) {
        return;
      }

      setContainerWidth(size.width);

      const isBelowBreakpoint = size.width < breakpoint;
      const prevBelowBreakpoint = prevBelowBreakpointRef.current;
      prevBelowBreakpointRef.current = isBelowBreakpoint;

      // Only act when the container crosses the threshold. Without this,
      // any resize while the container stays on the same side would call
      // `setCollapsed` and silently undo a user-initiated collapse/expand.
      if (prevBelowBreakpoint === isBelowBreakpoint) {
        return;
      }

      // Avoid spurious onCollapsedChange on the first measurement when the
      // value already matches the measured side.
      if (prevBelowBreakpoint === null && collapsed === isBelowBreakpoint) {
        return;
      }

      setCollapsed(isBelowBreakpoint);
    },
  });

  const isBelowBreakpoint = containerWidth !== 0 && containerWidth < breakpoint;

  return { containerWidth, collapsed, setCollapsed, isBelowBreakpoint };
}
